import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileUp, X } from "lucide-react";
import { C1_Dialog_PdfImport, type PdfImportPageSelection } from "../dialogs/C1_Dialog_PdfImport";
import type { ComponentProps, ReactNode } from "react";
import type { C1_Room } from "../model/C1_Room";
import type { C1_Floor } from "../model/C1_Floor";
import c1_state from "../model/C1_State";
import { RoomType, isSpecialExteriorRoomType, type C1_RoomData } from "../types/C1_ModelTypes";
import { itx } from "../../../src/translationItx/itx";
import {
  showErrToast,
  showToast,
  updateToastProgress,
  updateToastMessage,
  hideToast,
  getCurrentToastToken,
} from "../../../src/components/Toasts";
import "./C1_PdfImportButton.scss";
import type { C1ImportDraft } from "@estada/plan-analyzer";
import { resolveImportedRoomType } from "../lib/C1_importRoomType";
import { mergePdfImportFiles } from "../lib/C1_mergePdfImportFiles";
import { createBuildingPlanDocument } from "../../../src/models/createBuildingPlanDocument";
import { loadPdfImportPreview } from "../lib/C1_PdfImportPreview";
import { pdfBoundaryAxes } from "../lib/C1_pdfBoundaryAxes";
import { importedFreeWalls } from "../lib/C1_importFreeWalls";
import { singlePagePdfImportDefaults } from "../lib/C1_pdfImportFileNames";
import { C1_EventKey } from "../C1_EventKey";

/** PDF analysis remains isolated in a worker; only final materialization changes the project. */
export function C1_PdfImportButton({ createProject, onImported, floorImport, onBusyChange, beforeImport, buttonLabel, buttonClassName, buttonIcon }: {
  createProject?: (file: File, projectName?: string) => void;
  floorImport?: { name: string; tag: string; create: (selection: PdfImportPageSelection) => C1_Floor };
  onBusyChange?: (busy: boolean) => void;
  beforeImport?: () => boolean;
  buttonLabel?: string;
  buttonClassName?: string;
  buttonIcon?: ReactNode;
  onImported: (floor?: C1_Floor) => void;
}) {
  const input = useRef<HTMLInputElement>(null),
    controller = useRef<AbortController>(),
    mounted = useRef(true),
    toastToken = useRef<number>();
  const [busy, setBusy] = useState(false),
    [status, setStatus] = useState("");
  const [configuration, setConfiguration] = useState<ComponentProps<typeof C1_Dialog_PdfImport>>();
  const configurationResolve = useRef<() => void>();
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
      configurationResolve.current?.();
    };
  }, []);
  const reportProgress = (percentage: number, message: string) => {
    if (mounted.current) setStatus(message);
    if (toastToken.current !== undefined) {
      updateToastMessage(message, toastToken.current);
      updateToastProgress({ progress: percentage, toastToken: toastToken.current });
    }
  };

  async function importFile(selectedFiles: File | File[]) {
    if (controller.current) return;
    const projectOwner = state.projectMetadata;
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    onBusyChange?.(true);
    let successMessage: string | undefined, failureMessage: string | undefined;
    let importedFloor: C1_Floor | undefined;
    const checkAbort = () => {
      if (abort.signal.aborted) throw new DOMException("Abgebrochen", "AbortError");
    };
    const openProgress = async (message: string) => {
      await showToast({
        title: itx("c1.pdfImport.title", "PDF-Grundriss importieren"),
        text: message,
        progress: true,
        keepVisible: true,
      });
      toastToken.current = getCurrentToastToken();
    };
    try {
      await openProgress(itx("c1.pdfImport.reading", "PDF wird gelesen …"));
      const library = await import("@estada/plan-analyzer");
      const files = Array.isArray(selectedFiles) ? selectedFiles : [selectedFiles];
      c1_state.eventLogger.addEvent(C1_EventKey.PdfImportStarted, {
        fileCount: files.length,
        fileNames: files.map((file) => file.name),
        mode: floorImport ? "floor" : "project",
      });
      if (floorImport && files.length !== 1) throw new Error("Bitte genau eine PDF-Datei auswählen.");
      const pageCounts: number[] = [];
      const file = await mergePdfImportFiles(files, abort.signal, (completed, total, name) =>
        reportProgress((100 * completed) / total, itx("c1.pdfImport.merging", "PDF-Dateien zusammenfügen …") + " " + (completed + "/" + total) + " · " + name),
        (index, pageCount) => { pageCounts[index] = pageCount; },
      );
      const defaults = singlePagePdfImportDefaults(files, pageCounts);
      const bytes = new Uint8Array(await file.arrayBuffer());
      checkAbort();
      const inspection = await library.inspectPdf(bytes, {
        signal: abort.signal,
        onProgress: (value) => reportProgress((100 * (value.step - 1)) / value.totalSteps, value.phase),
      });
      const checkedPages = new Map<string, Awaited<ReturnType<typeof library.analyzePdfPage>>>();
      const analyzePage = async (page: number, scale: number, signal: AbortSignal) => {
        const key = page + ":" + scale;
        const cached = checkedPages.get(key);
        if (cached) return cached;
        const result = await library.analyzePdfPage(bytes, {
          page, fileName: file.name, options: { scale, freeWalls: true }, signal,
        });
        if (!result.draft.rooms.length && !importedFreeWalls(result.analysis, result.draft).length) {
          // Der Analyser erklärt in "warnings" konkret, woran die Erkennung scheiterte
          // (z. B. offene Wandenden, keine Wandpaare); das hilft bei der Planüberarbeitung.
          const details = (result.analysis?.warnings ?? [])
            .filter((warning) => warning && !warning.includes("--region"))
            .join(" ");
          throw new Error(
            itx("c1.pdfImport.noRooms", "Keine importierbaren Raumkonturen erkannt.") + (details ? " " + details : ""),
          );
        }
        checkedPages.set(key, result);
        return result;
      };
      checkAbort();
      if (toastToken.current !== undefined) {
        await hideToast(toastToken.current);
        toastToken.current = undefined;
      }
      const selections = await new Promise<PdfImportPageSelection[] | undefined>((resolve) => {
        const finish = (pages?: PdfImportPageSelection[]) => {
          configurationResolve.current = undefined;
          setConfiguration(undefined);
          resolve(pages);
        };
        configurationResolve.current = finish;
        setConfiguration({
          fileName: file.name,
          projectName: defaults?.projectName,
          pageDefaults: defaults?.pages,
          singleFloor: floorImport,
          inspection,
          checkPage: async (page, scale, signal) => { await analyzePage(page, scale, signal); },
          loadPreview: (page, signal) => loadPdfImportPreview(bytes, page, signal),
          onConfirm: finish,
          onCancel: () => finish(),
        });
      });
      if (!selections) return;
      if (floorImport && selections.length !== 1) throw new Error("Bitte genau eine PDF-Seite auswählen.");
      checkAbort();
      if (state.projectMetadata !== projectOwner)
        throw new Error(itx("c1.pdfImport.projectChanged", "Projekt wurde gewechselt. Bitte erneut importieren."));
      await openProgress(itx("c1.pdfImport.analyzing", "Ausgewählte Seiten werden analysiert …"));
      const totalSteps = selections.length * 16 + 2; // Final axis cleanup and commit are global steps.
      const percent = (steps: number) => (100 * steps) / totalSteps;
      const prepared: {
        selection: PdfImportPageSelection;
        analysis?: Awaited<ReturnType<typeof library.analyzePdfPage>>["analysis"];
        draft?: C1ImportDraft;
        warning?: string;
      }[] = [];
      for (const selection of selections) {
        if (selection.analysisWarning) {
          prepared.push({ selection, warning: selection.analysisWarning });
          continue;
        }
        const done = prepared.length * 13;
        const result = checkedPages.get(selection.page + ":" + selection.scale) ?? await library.analyzePdfPage(bytes, {
          page: selection.page,
          fileName: file.name,
          options: { scale: selection.scale, freeWalls: true },
          signal: abort.signal,
          onProgress: (value) =>
            reportProgress(
              percent(done + value.step - 1),
              "Seite " + selection.page + " · " + value.phase + " (" + (done + value.step) + "/" + totalSteps + ")",
            ),
        });
        checkAbort();
        if (!result.draft.rooms.length && !importedFreeWalls(result.analysis, result.draft).length)
          throw new Error(
            "Seite " +
              selection.page +
              ": " +
              itx("c1.pdfImport.noRooms", "Keine importierbaren Raumkonturen erkannt."),
          );
        prepared.push({ selection, ...result });
        reportProgress(
          percent(prepared.length * 13),
          "Seite " + selection.page + " · " + itx("c1.pdfImport.analysisDone", "Analyse abgeschlossen"),
        );
      }
      checkAbort();
      if (state.projectMetadata !== projectOwner)
        throw new Error(itx("c1.pdfImport.projectChanged", "Projekt wurde gewechselt. Bitte erneut importieren."));
      if (!floorImport) {
        if (!createProject) throw new Error("Der Projektimport ist nicht konfiguriert.");
        createProject(file, defaults?.projectName);
      }
      const undo = state.undoManager.beginDialogStep(itx("c1.pdfImport.undo", "PDF-Grundriss importieren"));
      const pageReports: {
        importedAxes: { sourceAxisId: string; axisId: string; reused: boolean; retained?: boolean }[];
        [key: string]: unknown;
      }[] = [];
      let roomCount = 0,
        freeWallCount = 0,
        axisCount = 0,
        openingTotal = 0,
        issueCount = 0;
      let firstRoom: ReturnType<typeof c1_state.currentFloor.addRoom> | undefined;
      let firstFloorId: string | undefined;
      try {
        if (floorImport) c1_state.project.enableIndividualFloorAxes();
        else state.settings.floorsHaveIndividualAxes = true;
        const originalDocument = createBuildingPlanDocument(file.name, bytes);
        for (const [pageIndex, preparedPage] of prepared.entries()) {
          checkAbort();
          const { selection, analysis, draft } = preparedPage;
          const floor = floorImport ? floorImport.create(selection) : c1_state.project.addFloor({
            name: selection.floorName,
            level: Math.max(...c1_state.project.floors.map((f) => f.level), -1) + 1,
            ownCabinet: false,
          });
          importedFloor ??= floor;
          floor.tag = selection.floorTag;
          if (floor.owner) floor.owner.name = selection.floorName;
          const pageSize = inspection.pages.find(page => page.page === selection.page);
          floor.setPdfSource({ documentId: originalDocument.id, page: selection.page,
            scale: selection.scale, originPdf: draft?.originPdf ?? { x: 0, y: 0 },
            pageSize: pageSize ? { width: pageSize.width, height: pageSize.height } : undefined });
          if (!firstFloorId) firstFloorId = floor.id;
          if (!draft) {
            const page = inspection.pages.find(page => page.page === selection.page);
            if (!page) throw new Error("Die PDF-Seitengröße wurde nicht gefunden.");
            const importedAxes = pdfBoundaryAxes(page, selection.scale, floor.id).map((data, index) => ({
              sourceAxisId: `pdf-page-boundary-${index}`,
              axisId: c1_state.project.addAxis(data).id,
              reused: false,
            }));
            const issues = [preparedPage.warning ?? "Keine importierbaren Raumkonturen erkannt."];
            pageReports.push({ page: selection.page, scale: selection.scale, floorId: floor.id,
              floorTag: floor.tag, floorName: (floor.owner?.name ?? ""), backgroundOnly: true, importedAxes, issues });
            issueCount += issues.length;
            continue;
          }
          const base = prepared.length * 13 + pageIndex * 3;
          const imported: { draft: C1ImportDraft["rooms"][number]; room: ReturnType<typeof floor.addRoom> }[] = [];
          const issues = [...draft.issues];
          let openingCount = 0;
          const openingBindings: unknown[] = [];
          const importedAxes: { sourceAxisId: string; axisId: string; reused: boolean }[] = [];
          reportProgress(percent(base), "Seite " + selection.page + " · " + itx("c1.pdfImport.axes", "Achsen anlegen"));
          for (let index = 0; index < draft.axes.length; index++) {
            if (abort.signal.aborted) throw new DOMException("Abgebrochen", "AbortError");
            const source = draft.axes[index];
            const existing = c1_state.project.axesForFloor(floor.id).find(
              (axis) => axis.kind === source.kind && Math.abs(axis.pos - source.pos) <= 0.000001,
            );
            const axis = existing ?? c1_state.project.addAxis({ kind: source.kind, pos: source.pos, floorId: floor.id });
            importedAxes.push({ sourceAxisId: source.sourceAxisId, axisId: axis.id, reused: !!existing });
            reportProgress(
              percent(base + (index + 1) / draft.axes.length),
              index + 1 + "/" + draft.axes.length + " · " + itx("c1.pdfImport.axes", "Achsen anlegen"),
            );
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
          reportProgress(percent(base + 1), itx("c1.pdfImport.rooms", "Räume anlegen"));
          for (let i = 0; i < draft.rooms.length; i++) {
            if (abort.signal.aborted) throw new DOMException("Abgebrochen", "AbortError");
            const roomDraft = draft.rooms[i];
            const recognizedType = resolveImportedRoomType(roomDraft.name, roomDraft.typeSuggestion);
            const type = recognizedType ?? RoomType.Office;
            if (!recognizedType) issues.push(roomDraft.name + ": Raumtyp vorläufig Büro; bitte prüfen.");
            const room = floor.addRoom({
              name: roomDraft.name,
              type,
              startPoint: roomDraft.startPoint,
              walls: roomDraft.walls.map((w) => ({ end: w.end, visible: w.visible })),
            } as C1_RoomData & { name: string; walls: typeof roomDraft.walls });
            imported.push({ draft: roomDraft, room });
            reportProgress(
              percent(base + 1 + (i + 1) / draft.rooms.length),
              i + 1 + "/" + draft.rooms.length + " · " + itx("c1.pdfImport.rooms", "Räume anlegen"),
            );
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
          imported[0]?.room.recalculateFloorWallTypes();
          const importedWallIds: string[] = [];
          for (const wall of importedFreeWalls(analysis, draft)) {
            importedWallIds.push(floor.addFreeWall(wall).id);
          }
          freeWallCount += importedWallIds.length;
          for (const [index, entry] of imported.entries()) {
            for (const source of entry.draft.openings) {
              const candidate = library.resolveOpeningWall(
                entry.draft,
                source,
                entry.room.walls?.asArray().flatMap((wall) => {
                  const points = entry.room.wallPoints(wall);
                  return points ? [{ id: wall.id, a: points.start, b: points.end, wall, points }] : [];
                }) ?? [],
              );
              if (!candidate) {
                issues.push(
                  source.sourceOpeningId +
                    ": Nach Wandnormalisierung kein passender gleichgerichteter Wandabschnitt mit ausreichender Länge.",
                );
                continue;
              }
              const side = entry.room.compatibilitySide(candidate.wall);
              let reflect = false;
              if (source.hingeM) {
                const positive =
                  side === "top" || side === "bottom"
                    ? source.hingeM.x > source.centerM.x
                    : source.hingeM.y > source.centerM.y;
                reflect = side === "top" || side === "right" ? !positive : positive;
              }
              const actualPartner = candidate.wall.partnerWall?.parent?.c1 as C1_Room | undefined;
              const kind =
                source.kind === "window" &&
                actualPartner &&
                actualPartner.className === "C1_Room" &&
                !isSpecialExteriorRoomType(entry.room.type) &&
                !isSpecialExteriorRoomType(actualPartner.type)
                  ? "door"
                  : source.kind;
              if (kind !== source.kind)
                issues.push(
                  source.sourceOpeningId + ": Innenraumfenster anhand der C1-Wandpartnerschaft als Tür importiert.",
                );
              const opening = entry.room.addOpening({
                kind,
                mode: "manual",
                side,
                _wallId: candidate.wall.id,
                width: source.width,
                offset: candidate.offset,
                reflect,
                doorType: kind === "door" ? (candidate.wall.partnerWall ? "interior" : "entrance") : undefined,
              });
              opening.setWallPlacement(candidate.wall, candidate.offset, reflect);
              const actual = opening.openingCenter();
              const expected = {
                x: candidate.a.x + (candidate.b.x - candidate.a.x) * candidate.offset,
                y: candidate.a.y + (candidate.b.y - candidate.a.y) * candidate.offset,
              };
              if (
                opening.room?.id !== entry.room.id ||
                opening.wall?.id !== candidate.wall.id ||
                Math.hypot(actual.x - expected.x, actual.y - expected.y) > 0.001
              )
                throw new Error(source.sourceOpeningId + ": Ungültige Raum-/Wandbindung nach dem Import.");
              openingBindings.push({
                sourceOpeningId: source.sourceOpeningId,
                openingId: opening.id,
                roomId: entry.room.id,
                wallId: candidate.wall.id,
                offset: candidate.offset,
                projectedCenterM: actual,
                sourceDistanceM: candidate.distance,
                partnerRoomId: opening.partnerRoom?.id,
              });
              const expectedPartner = source.partnerDraftRoomId
                ? imported.find((item) => item.draft.id === source.partnerDraftRoomId)?.room.id
                : undefined;
              if (expectedPartner && opening.kind === "door" && opening.partnerRoom?.id !== expectedPartner)
                issues.push(
                  source.sourceOpeningId +
                    ": Erkannter Nachbarraum stimmt nicht mit C1-Wandpartnerschaft überein; bitte prüfen.",
                );
              openingCount++;
            }
            reportProgress(
              percent(base + 2 + (index + 1) / imported.length),
              index + 1 + "/" + imported.length + " · " + itx("c1.pdfImport.openings", "Türen und Fenster anlegen"),
            );
            await new Promise((resolve) => setTimeout(resolve, 0));
          }

          if (!firstRoom) {
            firstRoom = imported[0]?.room;
          }
          roomCount += imported.length;
          axisCount += importedAxes.length;
          openingTotal += openingCount;
          issueCount += issues.length;
          pageReports.push({
            page: selection.page,
            scale: selection.scale,
            floorId: floor.id,
            floorTag: floor.tag,
            floorName: (floor.owner?.name ?? ""),
            analysis,
            draft,
            importedAxes,
            openingBindings,
            importedFreeWallIds: importedWallIds,
            imported: imported.map((entry) => ({
              sourceRoomId: entry.draft.sourceRoomId,
              roomId: entry.room.id,
              wallIds: entry.room.walls?.asArray().map((w) => w.id),
            })),
            issues,
          });
        }
        checkAbort();
        reportProgress(
          percent(prepared.length * 16),
          itx("c1.pdfImport.mergeAxes", "Achsen je Etage zusammenführen und bereinigen"),
        );
        const cleanupFloorId = floorImport ? firstFloorId : undefined;
        c1_state.project.cleanupUnusedAxes(false, cleanupFloorId);
        const mergedAxisIds = c1_state.project.mergeAxesAtMidpoints(0.2, cleanupFloorId);
        c1_state.project.cleanupUnusedAxes(false, cleanupFloorId);
        const retainedAxisIds = new Set(c1_state.allAxes.map((axis) => axis.id));
        for (const page of pageReports)
          for (const axis of page.importedAxes) {
            axis.axisId = mergedAxisIds.get(axis.axisId) ?? axis.axisId;
            axis.retained = retainedAxisIds.has(axis.axisId);
          }
        axisCount = new Set(
          pageReports.flatMap((page) => page.importedAxes.filter((axis) => axis.retained).map((axis) => axis.axisId)),
        ).size;
        reportProgress(percent(prepared.length * 16 + 1), itx("c1.pdfImport.saving", "Import abschließen"));
        await new Promise((resolve) => setTimeout(resolve, 0));
        checkAbort();
        const { generateTags } = await import("../estada/C1_generateTags");
        checkAbort();
        generateTags(c1_state.project);
        if (floorImport && importedFloor) importedFloor.tag = selections[0].floorTag;
        await undo.commit();
      } catch (error) {
        await undo.rollback();
        throw error;
      }
      if (firstFloorId) c1_state.setCurrentFloor(firstFloorId);
      c1_state.pdfOverlayOpacity.value = 50;
      c1_state.editMode.value = "building";
      c1_state.setSelection(firstRoom);
      c1_state.resetViewOffset();
      const text =
        prepared.length +
        " " +
        itx("c1.pdfImport.importedPages", "Seiten") +
        ", " +
        roomCount +
        " " +
        itx("c1.pdfImport.roomCount", "Räume") +
        ", " +
        axisCount +
        " " +
        itx("c1.pdfImport.axisCount", "Achsen") +
        ", " +
        openingTotal +
        " " +
        itx("c1.pdfImport.openingCount", "Öffnungen importiert") +
        (freeWallCount ? ", " + freeWallCount + " " + itx("c1.freeWall.plural", "freie Wände") : "") +
        (issueCount ? " · " + issueCount + " " + itx("c1.pdfImport.reviewCount", "Prüfhinweise") : "");
      successMessage = text;
      reportProgress(100, text);
      c1_state.eventLogger.addEvent(C1_EventKey.PdfImportCompleted, {
        fileCount: files.length,
        pages: prepared.length,
        scales: prepared.map((page) => page.selection.scale),
        rooms: roomCount,
        axes: axisCount,
        openings: openingTotal,
        freeWalls: freeWallCount,
        issues: issueCount,
        mode: floorImport ? "floor" : "project",
      });
    } catch (error) {
      if (mounted.current) {
        const message = error instanceof Error ? error.message : String(error);
        setStatus(message);
        if (error instanceof DOMException && error.name === "AbortError") {
          c1_state.eventLogger.addEvent(C1_EventKey.PdfImportCancelled, { stage: "processing" });
        } else {
          failureMessage = message;
          c1_state.eventLogger.addEvent(C1_EventKey.PdfImportFailed, { message });
        }
      }
    } finally {
      if (toastToken.current !== undefined) {
        await hideToast(toastToken.current);
        toastToken.current = undefined;
      }
      controller.current = undefined;
      if (mounted.current) {
        setBusy(false);
        onBusyChange?.(false);
        if (failureMessage) showErrToast(failureMessage);
        else if (successMessage) {
          void showToast({ text: successMessage, isSuccess: true });
          onImported(importedFloor);
        }
      }
    }
  }
  const label = buttonLabel ?? (floorImport ? itx("floorDialog.importPdf", "Aus PDF importieren") : itx("c1.pdfImport.shortButton", "PDF Import"));
  const icon = buttonIcon ?? <FileUp size={16} style={{ width: 16, height: 16 }} />;
  return (
    <div className="C1_pdfImport" style={buttonClassName ? {display: "contents"} : undefined}>
      {configuration && (floorImport
        ? createPortal(<div className="C1" style={{ display: "contents" }}><C1_Dialog_PdfImport {...configuration} portal={false} /></div>,
          document.getElementById("dialogWrapper") ?? document.body)
        : <C1_Dialog_PdfImport {...configuration} />)}
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        multiple={!floorImport}
        hidden
        data-testid="c1-pdf-import-file"
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? []);
          event.currentTarget.value = "";
          if (files.length) void importFile(files);
        }}
      />
      <button
        type="button"
        className={buttonClassName ?? "button"}
        data-testid="c1-pdf-import"
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={status || itx("c1.pdfImport.title", "PDF-Grundriss importieren")}
        data-tooltip-place="bottom"
        disabled={busy}
        onClick={() => { if (!beforeImport || beforeImport()) input.current?.click(); }}
      >
        {buttonClassName ? <>{icon}<span>{label}</span></> : <>
          <span className="C1_menuButtonIcon">{icon}</span>
          <span className="C1_menuButtonText">{label}</span>
        </>}
      </button>
      {busy &&
        !configuration &&
        createPortal(
          <button
            className="C1_pdfImportAbort"
            type="button"
            onClick={() => controller.current?.abort()}
            aria-label={itx("c1.pdfImport.abort", "Import abbrechen")}
          >
            <X size={14} /> {itx("c1.pdfImport.abort", "Import abbrechen")}
          </button>,
          document.body,
        )}
    </div>
  );
}
